import { useState, useEffect } from "react";
import { Bell, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  useListActiveTags,
  getListActiveTagsQueryKey,
  useGetMyTagSubscriptions,
  getGetMyTagSubscriptionsQueryKey,
  useUpdateMyTagSubscriptions,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import IndustryFocusCombobox from "@/components/industry-focus-combobox";

type SubscriptionType = "community" | "job";

interface TagSubscriptionManagerProps {
  /** Which subscription bucket to manage. Defaults to "community" for backward compatibility. */
  subscriptionType?: SubscriptionType;
  onSaveSuccess?: () => void;
}

export function TagSubscriptionManager({
  subscriptionType = "community",
  onSaveSuccess,
}: TagSubscriptionManagerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isJob = subscriptionType === "job";

  const communityTagsQuery = useListActiveTags({
    query: { queryKey: getListActiveTagsQueryKey(), enabled: !isJob },
  });

  const communityTags = communityTagsQuery.data ?? [];
  const tagsLoading = isJob ? false : communityTagsQuery.isLoading;

  const hasAnyActiveTags = isJob ? true : communityTags.length > 0;

  const subsQueryKey = getGetMyTagSubscriptionsQueryKey({ subscriptionType });

  const { data: currentSubs = [], isLoading: subsLoading } = useGetMyTagSubscriptions(
    { subscriptionType },
    { query: { queryKey: subsQueryKey, retry: false } },
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!subsLoading && !initialized) {
      setSelected(new Set(currentSubs));
      setInitialized(true);
    }
  }, [currentSubs, subsLoading, initialized]);

  const updateMutation = useUpdateMyTagSubscriptions();

  function toggleTag(tag: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  }

  function handleSave() {
    const optimisticTags = Array.from(selected);
    const previousTags = queryClient.getQueryData<string[]>(subsQueryKey) ?? [];

    queryClient.setQueryData(subsQueryKey, optimisticTags);

    updateMutation.mutate(
      { data: { tags: optimisticTags, subscriptionType } },
      {
        onSuccess: (newTags) => {
          setSelected(new Set(newTags));
          queryClient.setQueryData(subsQueryKey, newTags);
          void queryClient.invalidateQueries({ queryKey: subsQueryKey });
          toast({
            title: isJob ? "Job alerts saved" : "Tag alerts saved",
            description:
              newTags.length === 0
                ? "You are no longer subscribed to any tags."
                : `Subscribed to ${newTags.length} tag${newTags.length === 1 ? "" : "s"}.`,
          });
          onSaveSuccess?.();
        },
        onError: () => {
          queryClient.setQueryData(subsQueryKey, previousTags);
          toast({
            title: "Save failed",
            description: "Could not save your tag preferences. Please try again.",
            variant: "destructive",
          });
        },
      },
    );
  }

  const isLoading = tagsLoading || subsLoading;

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-20 rounded-full bg-zinc-800" />
          ))}
        </div>
        <Skeleton className="h-9 w-24 rounded-md bg-zinc-800" />
      </div>
    );
  }

  if (!hasAnyActiveTags) {
    return (
      <p className="text-sm text-zinc-500 font-sans">
        No tags are currently in use on the community board.
      </p>
    );
  }

  const renderChip = (tag: string) => {
    const isSelected = selected.has(tag);
    return (
      <button
        key={tag}
        onClick={() => toggleTag(tag)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
          isSelected
            ? "bg-primary/15 text-primary border-primary/40 hover:bg-primary/25"
            : "bg-zinc-800/80 text-zinc-400 border-white/10 hover:bg-zinc-700/80 hover:text-zinc-200 hover:border-white/20"
        }`}
      >
        {isSelected && <Check className="w-3 h-3 shrink-0" />}
        {tag}
      </button>
    );
  };

  const saveControls = (
    <div className="flex items-center gap-3">
      <Button
        onClick={handleSave}
        disabled={updateMutation.isPending}
        className="h-9 bg-primary hover:bg-primary/90 text-white font-bold uppercase text-xs px-4"
      >
        {updateMutation.isPending ? (
          <>
            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Bell className="w-3.5 h-3.5 mr-2" />
            Save Alerts
          </>
        )}
      </Button>
      {selected.size > 0 && (
        <span className="text-xs text-zinc-500 font-sans">
          {selected.size} tag{selected.size === 1 ? "" : "s"} selected
        </span>
      )}
    </div>
  );

  if (isJob) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-zinc-400 font-sans">
          Select the disciplines and titles you want to be notified about when new opportunities are posted.
        </p>
        <IndustryFocusCombobox
          value={Array.from(selected)}
          onChange={(v) => setSelected(new Set(v))}
          placeholder="Search disciplines and titles..."
        />
        {saveControls}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {communityTags.map(renderChip)}
      </div>
      {saveControls}
    </div>
  );
}
